// page-meeting-tab-views.ts — Contenu HTML de chaque onglet de la fiche
// réunion (participants, actions, indicateurs, notes, support, CR,
// génération) et les deux écrans d'attente.
//
// Extrait de `renderMeetingPage` le 2026-09-08 (point 16). Rendu pur à
// partir d'un contexte explicite ; le câblage et les chargements asynchrones
// restent dans la page et les modules d'onglet (page-meeting-notes, -cr,
// -generation, -draft-actions).
//
// Globaux ambiants : esc, t, iaCreditBadge (core-globals.d.ts).

import type { MeetingPageState, TabKey } from '../domain/meeting-form.js';
import { raciBlockHtml } from './page-meeting-invitees.js';
import { renderNoCommitteePlaceholder, renderSaveFirstPlaceholder } from './page-meeting-steps.js';
import { viewActionsHash } from './page-meeting-actions-tab.js';

export interface TabViewsContext {
  meeting: { id: number } | null;
  state: MeetingPageState;
  users: ReadonlyArray<{ id: number; trigram: string; name: string; is_team: boolean }>;
  projects: ReadonlyArray<{ id: number; code: string; name: string }>;
  committees: ReadonlyArray<{ id: number; code: string; name: string }>;
  structures: ReadonlyArray<{ id: number; code: string; label: string }>;
  isAnimator: boolean;
  isOpen: boolean;
  isClosed: boolean;
  /** CTA « Fermer la réunion » dans l'onglet CR (animateur, non fermée). */
  showCloseBar: boolean;
  /** Bloc Informations verrouillé : le CTA « Proposer » l'est aussi. */
  infoDisabled: boolean;
}

export function participantsTabHtml(ctx: TabViewsContext): string {
  const lockedAttr = ctx.infoDisabled ? ' disabled' : '';
  const animatorsHtml = raciBlockHtml(
    ctx.users,
    'mp-animators',
    ctx.state.animatorIds,
    t('pageMeetingsRecords.fieldAnimators'),
  );
  const participantsHtml = raciBlockHtml(
    ctx.users,
    'mp-participants',
    ctx.state.participantIds,
    t('pageMeetingsRecords.fieldParticipants'),
    { withCount: true },
  );
  return `
      <div class="card">
        <h2 style="font-size:15px;margin:0 0 12px 0">${esc(t('pageMeeting.blockParticipants'))}</h2>
        <div class="form-row" style="display:flex;gap:12px;flex-wrap:wrap">
          ${animatorsHtml}
          ${participantsHtml}
        </div>
        <div style="margin-top:12px">
          <button type="button" id="mp-propose-btn" class="btn btn-secondary"${lockedAttr}>
            ${esc(t('pageMeeting.btnProposeParticipants'))}
          </button>
        </div>
        <div id="mp-proposal-host" style="margin-top:12px"></div>
      </div>`;
}

/** Squelette + CTA ; la table est chargée ensuite (loadActionsTab). */
export function actionsTabHtml(): string {
  return `
      <div class="card">
        <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:12px;flex-wrap:wrap">
          <h2 style="font-size:15px;margin:0">${esc(t('pageMeeting.tab.actions'))}</h2>
          <button type="button" id="mp-actions-update-btn" class="btn btn-secondary">
            ${esc(t('pageMeeting.btnUpdateActions'))}
          </button>
        </div>
        <div id="mp-actions-host">
          <div class="loading">${esc(t('common.loading'))}</div>
        </div>
      </div>`;
}

export function indicatorsTabHtml(): string {
  return `
      <div class="card">
        <h2 style="font-size:15px;margin:0 0 4px 0">${esc(t('pageMeeting.tab.indicators'))}</h2>
        <p style="margin:0 0 14px 0;color:var(--text2);font-size:13px">${esc(t('pageMeeting.indicators.intro'))}</p>
        <div id="mp-indicators-host">
          <div class="loading">${esc(t('common.loading'))}</div>
        </div>
      </div>`;
}

/**
 * Onglet Notes : split 50/50. Gauche, l'éditeur de notes ; droite, la table
 * « Nouvelles actions » (rendue ensuite par page-meeting-draft-actions).
 * L'animateur a en plus « Ouvrir la réunion » (tant qu'elle n'est pas ouverte
 * active) et un lien vers #actions pré-filtré sur les actions liées.
 */
export function notesTabHtml(ctx: TabViewsContext): string {
  const { state, isOpen, isClosed } = ctx;
  let openActionsBarHtml = '';
  if (ctx.isAnimator && ctx.meeting) {
    const href = viewActionsHash(ctx.meeting.id, state);
    const openBtnHtml =
      !isOpen || isClosed
        ? `<button type="button" id="mp-meeting-open" class="btn btn-primary">
              ${esc(t('pageMeeting.notes.btnOpenMeeting'))}
            </button>`
        : '';
    openActionsBarHtml = `<div style="margin-bottom:10px;display:flex;justify-content:flex-end;gap:8px;align-items:center;flex-wrap:wrap">
          ${openBtnHtml}
          <a href="${esc(href)}" target="_blank" rel="noopener"
             class="btn btn-secondary btn-sm"
             style="display:inline-flex;align-items:center;gap:6px">
            ↗ ${esc(t('pageMeeting.notes.btnOpenActionsTab'))}
          </a>
        </div>`;
  }
  // Bandeau « Réunion fermée » pour tous les rôles : c'est un état partagé.
  const closedBannerHtml = isClosed
    ? `<div style="margin-bottom:10px;padding:8px 12px;background:var(--bg2);border:1px solid var(--border);border-radius:var(--radius);font-size:12px;color:var(--text2)">
          🔒 ${esc(t('pageMeeting.notes.closedBanner'))}
        </div>`
    : '';
  const editorActionsHtml = isClosed
    ? ''
    : `<div style="display:flex;gap:8px;margin-top:10px;justify-content:flex-end;align-items:center">
            <span class="mp-notes-feedback" style="font-size:12px;color:var(--success);margin-right:auto"></span>
            <button type="button" id="mp-notes-clear" class="btn btn-secondary">${esc(t('pageMeeting.notes.btnClear'))}</button>
            <button type="button" id="mp-notes-save" class="btn btn-primary mp-notes-save-btn">${esc(t('pageMeeting.notes.btnSave'))}</button>
          </div>`;
  const editorToolbarHtml = isClosed
    ? ''
    : `<div id="mp-notes-toolbar" style="display:flex;gap:4px;padding:6px 8px;border:1px solid var(--border2);border-radius:var(--radius) var(--radius) 0 0;background:var(--bg2)">
            <button type="button" data-cmd="bold" style="font-weight:bold;min-width:28px;padding:2px 6px;border:1px solid var(--border2);border-radius:4px;background:var(--bg);cursor:pointer">G</button>
            <button type="button" data-cmd="italic" style="font-style:italic;min-width:28px;padding:2px 6px;border:1px solid var(--border2);border-radius:4px;background:var(--bg);cursor:pointer">I</button>
            <button type="button" data-cmd="underline" style="text-decoration:underline;min-width:28px;padding:2px 6px;border:1px solid var(--border2);border-radius:4px;background:var(--bg);cursor:pointer">S</button>
            <button type="button" data-cmd="insertUnorderedList" style="min-width:28px;padding:2px 6px;border:1px solid var(--border2);border-radius:4px;background:var(--bg);cursor:pointer">≡</button>
          </div>`;
  // En lecture seule, coins ronds tout autour (pas de toolbar soudée).
  const editorRadius = isClosed
    ? 'border-radius:var(--radius)'
    : 'border-top:none;border-radius:0 0 var(--radius) var(--radius)';
  // Doublure haute du CTA Enregistrer : reste visible quand l'éditeur est
  // long. Mêmes handlers via la classe `mp-notes-save-btn`.
  const saveTopBarHtml = isClosed
    ? ''
    : `<div style="display:flex;justify-content:flex-end;align-items:center;gap:10px;margin-bottom:6px">
          <span class="mp-notes-feedback" style="font-size:12px;color:var(--success)"></span>
          <button type="button" class="btn btn-primary mp-notes-save-btn">${esc(t('pageMeeting.notes.btnSave'))}</button>
        </div>`;
  const readOnlyStyle = isClosed ? ';background:var(--bg2);color:var(--text2)' : '';
  // La règle globale `.card + .card { margin-top:16px }` décalerait la 2e
  // card dans ce flex : neutralisée inline sur les deux.
  return `
      ${closedBannerHtml}
      ${openActionsBarHtml}
      <div style="display:flex;gap:16px;flex-wrap:wrap;align-items:stretch">
        <div class="card" style="flex:1;min-width:320px;min-height:640px;margin-top:0;display:flex;flex-direction:column">
          <h2 style="font-size:15px;margin:0 0 12px 0">${esc(t('pageMeeting.tab.notes'))}</h2>
          ${saveTopBarHtml}
          ${editorToolbarHtml}
          <div id="mp-notes-editor"
            contenteditable="${isClosed ? 'false' : 'true'}"
            spellcheck="true"
            style="flex:1;min-height:480px;padding:10px 12px;border:1px solid var(--border2);${editorRadius};outline:none;overflow-y:auto;line-height:1.6${readOnlyStyle}">
            <span style="color:var(--muted)">${esc(t('common.loading'))}</span>
          </div>
          ${editorActionsHtml}
        </div>

        <div class="card" style="flex:1;min-width:360px;min-height:640px;margin-top:0;display:flex;flex-direction:column">
          <h2 style="font-size:15px;margin:0 0 12px 0">${esc(t('pageMeeting.notes.draftTitle'))}</h2>
          <div id="mp-draft-host" style="flex:1;overflow:auto"><div class="loading">${esc(t('common.loading'))}</div></div>
        </div>
      </div>`;
}

export function supportTabHtml(): string {
  return `
      <div class="card">
        <h2 style="font-size:15px;margin:0 0 12px 0">${esc(t('pageMeeting.tab.support'))}</h2>
        <p style="font-size:13px;color:var(--text2);margin:0 0 16px 0;line-height:1.6">${esc(t('pageMeeting.support.intro'))}</p>
        <button type="button" id="mp-support-launch" class="btn btn-primary">${esc(t('pageMeeting.support.btnLaunch'))}</button>
      </div>`;
}

export function crTabHtml(ctx: Pick<TabViewsContext, 'showCloseBar' | 'isClosed'>): string {
  const closeBarHtml = ctx.showCloseBar
    ? `<div style="display:flex;justify-content:flex-end;margin-bottom:10px">
            <button type="button" id="mp-meeting-close" class="btn btn-primary">
              ${esc(t('pageMeeting.cr.btnCloseMeeting'))}
            </button>
          </div>`
    : '';
  const closedBannerHtml = ctx.isClosed
    ? `<div style="margin-bottom:10px;padding:8px 12px;background:var(--bg2);border:1px solid var(--border);border-radius:var(--radius);font-size:12px;color:var(--text2)">
          🔒 ${esc(t('pageMeeting.cr.closedBanner'))}
        </div>`
    : '';
  const creditHtml = iaCreditBadge();
  return `
      ${closeBarHtml}
      ${closedBannerHtml}
      <div class="card">
        <h2 style="font-size:15px;margin:0 0 12px 0">${esc(t('pageMeeting.tab.cr'))}</h2>
        <p style="margin:0 0 14px 0;color:var(--text2);font-size:13px">${esc(t('pageMeetingsRecords.modalCR.noRedownload'))}</p>
        <div style="margin-bottom:14px">
          <label class="form-label">${esc(t('pageMeetingsRecords.modalCR.supportLabel'))}</label>
          <input type="file" id="mp-cr-file" class="form-control" accept=".docx,.pptx,.xlsx,.xls" style="padding:6px">
          <div class="form-hint">${esc(t('pageMeetingsRecords.modalCR.supportHint'))}</div>
        </div>
        <div style="margin-bottom:14px">
          <label class="form-label" style="margin-bottom:6px;display:block">${esc(t('pageMeetingsRecords.modalCR.notesLabel'))}</label>
          <div id="mp-cr-notes-wrap">
            <span style="color:var(--muted);font-size:13px">${esc(t('pageMeetingsRecords.modalCR.notesLoading'))}</span>
          </div>
        </div>
        <div style="display:flex;align-items:center;gap:12px">
          <button type="button" id="mp-cr-generate" class="btn btn-primary">${esc(t('pageMeetingsRecords.modalCR.btnGenerate'))}${creditHtml}</button>
          <div id="mp-cr-status" style="font-size:13px;color:var(--text2);min-height:18px"></div>
        </div>
      </div>`;
}

/**
 * Onglet « Générer les actions » : bloc « Analyser un CR » toujours visible
 * (nouvelle analyse possible à tout moment, même réunion fermée), puis les
 * CTA d'initialisation en masse (comité, projet, structure de la réunion,
 * chacun présent seulement s'il est rattaché) et l'hôte des brouillons.
 */
export function generationTabHtml(
  ctx: Pick<TabViewsContext, 'state' | 'projects' | 'committees' | 'structures'>,
): string {
  const { state } = ctx;
  const creditHtml = iaCreditBadge();
  const analyseBlockHtml = `
      <div class="card">
        <h2 style="font-size:15px;margin:0 0 10px 0">${esc(t('pageMeeting.generation.analyse.title'))}</h2>
        <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap">
          <label class="btn btn-secondary btn-sm" style="cursor:pointer;display:inline-flex;align-items:center;gap:6px">
            <span id="mp-analyse-file-label">${esc(t('pageMeeting.generation.analyse.pickFile'))}</span>
            <input type="file" id="mp-analyse-file" accept=".docx,.pptx" style="display:none">
          </label>
          <span id="mp-analyse-hint" style="font-size:11px;color:var(--text3)">${esc(t('pageMeeting.generation.analyse.hint'))}</span>
          <span style="flex:1"></span>
          <button type="button" id="mp-analyse-launch" class="btn btn-primary btn-sm" disabled>${esc(t('pageMeeting.generation.analyse.btnRun'))}${creditHtml}</button>
        </div>
        <div id="mp-analyse-status" style="font-size:12px;color:var(--text2);min-height:16px;margin-top:8px"></div>
      </div>`;
  const codeLabel = (code: string, name: string): string => (code ? `${code} — ${name}` : name);
  const bulk: Array<{ id: string; titleKey: string; label: string } | null> = [
    (() => {
      const c = state.committeeId ? ctx.committees.find((x) => x.id === state.committeeId) : null;
      return c
        ? {
            id: 'mp-gen-bulk-committee',
            titleKey: 'pageMeeting.generation.bulk.committeeTitle',
            label: codeLabel(c.code, c.name),
          }
        : null;
    })(),
    (() => {
      const p = state.projectId ? ctx.projects.find((x) => x.id === state.projectId) : null;
      return p
        ? {
            id: 'mp-gen-bulk-project',
            titleKey: 'pageMeeting.generation.bulk.projectTitle',
            label: codeLabel(p.code, p.name),
          }
        : null;
    })(),
    (() => {
      const s = state.structureId ? ctx.structures.find((x) => x.id === state.structureId) : null;
      return s
        ? {
            id: 'mp-gen-bulk-structure',
            titleKey: 'pageMeeting.generation.bulk.structureTitle',
            label: `${s.code} — ${s.label}`,
          }
        : null;
    })(),
  ];
  const bulkBtnsHtml = bulk
    .filter((b): b is NonNullable<typeof b> => b !== null)
    .map(
      (b) =>
        `<button type="button" id="${b.id}" class="btn btn-secondary btn-sm" title="${esc(t(b.titleKey))}">${esc(b.label)}</button>`,
    )
    .join('');
  const bulkBarHtml = bulkBtnsHtml
    ? `<div style="display:flex;gap:8px;flex-wrap:wrap;align-items:center;margin-bottom:12px"><span style="font-size:12px;color:var(--text2)">${esc(t('pageMeeting.generation.bulk.prefix'))}</span>${bulkBtnsHtml}</div>`
    : '';
  return `
      ${analyseBlockHtml}
      <div class="card">
        <h2 style="font-size:15px;margin:0 0 4px 0">${esc(t('pageMeeting.tab.generation'))}</h2>
        <p style="margin:0 0 14px 0;color:var(--text3);font-size:12px">${esc(t('pageMeeting.generation.intro'))}</p>
        ${bulkBarHtml}
        <div id="mp-gen-host"><div class="loading">${esc(t('common.loading'))}</div></div>
      </div>`;
}

/**
 * Contenu de l'onglet actif. En création, seul « Participants » a un contenu
 * (les autres ont besoin d'un id) ; une réunion sans comité (brouillon
 * calendrier) garde les autres onglets verrouillés jusqu'au rattachement.
 */
export function tabContentHtml(ctx: TabViewsContext): string {
  const tab: TabKey = ctx.state.activeTab;
  if (!ctx.meeting && tab !== 'participants') return renderSaveFirstPlaceholder();
  if (ctx.meeting && !ctx.state.committeeId && tab !== 'participants') {
    return renderNoCommitteePlaceholder();
  }
  switch (tab) {
    case 'actions':
      return actionsTabHtml();
    case 'indicators':
      return indicatorsTabHtml();
    case 'support':
      return supportTabHtml();
    case 'notes':
      return notesTabHtml(ctx);
    case 'cr':
      return crTabHtml(ctx);
    case 'generation':
      return generationTabHtml(ctx);
    case 'participants':
    default:
      return participantsTabHtml(ctx);
  }
}
